import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import API_ENDPOINTS from "@/util/api";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { Form, Popconfirm } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const MISC_NUMBERS_HEADERS = [
  { key: "miscNumber", text: "Description" },
  { key: "number", text: "Number" },
];

const PersonalMiscNumbersInfo = ({ startingPoint = "" } = {}) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const fullSearch = useSelector((state) => state.fullSearch);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). Shared across modules
  // (Bookings, Warrants, ...) — gridId must carry the caller's module
  // context via startingPoint, same fix as AddressesList.jsx, or two
  // different screens end up sharing one saved column layout.
  const {
    headers: miscHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    `${startingPoint || "Bookings"}:MiscellaneousNumber`,
    MISC_NUMBERS_HEADERS
  );

  const [showMiscModal, setShowMiscModal] = useState(false);
  const [form] = Form.useForm();
  const [selectMisc, setSelectMisc] = useState(null);
  const [miscData, setMiscData] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const inputRef = useRef(null);

  // -----------------------------------------------------------------------
  // Sub-module security checks — computed once, reused across buttons/table
  // -----------------------------------------------------------------------
  const canAdd = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.subModule,
    LABELS_CAPITAL.ADD,
    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
    SUB_MODULES_LIST_WITH_ID.Arrest_Report
  );

  const canView = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.subModule,
    LABELS_CAPITAL.VIEW,
    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
    SUB_MODULES_LIST_WITH_ID.Arrest_Report
  );

  const canModify = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.subModule,
    LABELS_CAPITAL.MODIFY,
    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
    SUB_MODULES_LIST_WITH_ID.Arrest_Report
  );

  const canDelete = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.subModule,
    LABELS_CAPITAL.DELETE,
    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
    SUB_MODULES_LIST_WITH_ID.Arrest_Report
  );

  // -----------------------------------------------------------------------
  // Sync bookings data → local miscData
  // -----------------------------------------------------------------------
  useEffect(() => {
    const raw = bookings?.bio?.[0]?.miscellaneousNumber;
    const mapped = raw?.map((misc) => ({
      ...misc,
      miscNumber: getRefListNameByID("Misc_Number", misc?.miscNumberID),
      id: uuidv4(),
    }));
    setMiscData(mapped);
  }, [bookings]);

  // Sync selected row → form
  useEffect(() => {
    if (isNonEmptyVariable(selectMisc)) {
      form.setFieldsValue(selectMisc);
    } else {
      form.resetFields();
    }
  }, [selectMisc]);

  // Disable save when editing without MODIFY permission
  useEffect(() => {
    setIsDisabled(showEdit ? !canModify : false);
  }, [showEdit]);

  // -----------------------------------------------------------------------
  // Modal handlers
  // -----------------------------------------------------------------------
  const openModal = () => setShowMiscModal(true);

  const closeModal = () => {
    setShowMiscModal(false);
    setSelectMisc(null);
    setShowEdit(false);
    form.resetFields();
  };

  const addMisc = () => {
    setSelectMisc(null);
    setShowEdit(false);
    openModal();
  };

  const editMisc = () => openModal();

  const selectMiscDataFn = (value) => {
    setSelectMisc(value);
    setShowEdit(true);
  };

  // -----------------------------------------------------------------------
  // Save / Delete
  // -----------------------------------------------------------------------
  const saveClick = () => {
    form
      .validateFields()
      .then((values) => {
        const md = showEdit
          ? (miscData || []).map((m) =>
              m?.id === selectMisc?.id ? { ...m, ...values } : m
            )
          : [...(miscData || []), values];

        dispatch(
          setBookings({
            bio: [{ ...bookings?.bio?.[0], miscellaneousNumber: md }],
          })
        );
        closeModal();
      })
      .catch(() => {});
  };

  const deleteDC = () => {
    const md = miscData?.filter((p) => p?.id !== selectMisc?.id);
    dispatch(
      setBookings({
        bio: [{ ...bookings?.bio?.[0], miscellaneousNumber: md }],
      })
    );

    if (isNonEmptyVariable(selectMisc?.miscellaneousNumberID)) {
      dispatch(
        setDeleteAPis(
          `${API_ENDPOINTS.miscellaneousNumber}/id/${selectMisc.miscellaneousNumberID}`
        )
      );
    }
    closeModal();
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <div />
          <StyledFlex gap="10px">

            {/* Add */}
            <Button
              htmlType="button"
              onClick={addMisc}
              disabled={!bookingsFieldsEnabled || !canAdd || isConfiguring}
            >
              Add Misc Numbers
            </Button>

            {/* Details */}
            <Button
              htmlType="button"
              disabled={!showEdit || !canView || isConfiguring}
              onClick={editMisc}
            >
              Details
            </Button>

            {/* Delete */}
            <Popconfirm
              title="Delete the Misc Numbers"
              description="Are you sure you want to delete this MISC number?"
              onConfirm={deleteDC}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <Button
                disabled={
                  !showEdit || !bookingsFieldsEnabled || !canDelete || isConfiguring
                }
              >
                Delete
              </Button>
            </Popconfirm>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {canView &&
              (!isConfiguring ? (
                <Button
                  htmlType="button"
                  onClick={startConfiguring}
                >
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button htmlType="button">
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              ))}

          </StyledFlex>
        </TableHeader>

        {canView && (
          <Table
            ref={dataTableRef}
            headers={miscHeaders}
            draggable={isConfiguring}
            data={miscData}
            onRowClick={selectMiscDataFn}
            onRowDoubleClick={(value) => {
              selectMiscDataFn(value);
              editMisc();
            }}
            selected={selectMisc === null}
          />
        )}
      </InmateDetailsTableContainer>

      {/* Modal — field-level security via FieldSecurityProvider */}
      {showMiscModal && (
        <Modal
          header="Misc Numbers"
          show={showMiscModal}
          onHide={closeModal}
          footerActions={[
            {
              key: "save",
              text: "Save",
              onClick: saveClick,
              disabled: !bookingsFieldsEnabled || isDisabled,
            },
          ]}
          footerCloseAction={() => setShowEdit(false)}
          footerCloseActionText="Cancel"
        >
          <FieldSecurityProvider
            entity="MiscellaneousNumber"
            bookingsFieldsEnabled={bookingsFieldsEnabled}
            startingPoint={"Bookings"}
          >
            <Form form={form} disabled={!bookingsFieldsEnabled}>
              <StyledGridRow gutter={16}>

                {/* Type */}
                <StyledGridCol span={16}>
                  <SecureFormItem
                    fieldName="miscNumberID"
                    formItemProps={{
                      name: "miscNumberID",
                      label: "Type",
                      required: !fullSearch,
                      rules: !fullSearch
                        ? [{ required: true, message: "You must enter a type of number" }]
                        : [],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("Misc_Number", false, false, true)}
                      placeholder="Type"
                      showSearch
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Number */}
                <StyledGridCol span={8}>
                  <SecureFormItem
                    fieldName="number"
                    formItemProps={{
                      name: "number",
                      label: "Number",
                      required: !fullSearch,
                      rules: !fullSearch
                        ? [{ required: true, message: "You must enter a number!" }]
                        : [],
                    }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

              </StyledGridRow>
            </Form>
          </FieldSecurityProvider>
        </Modal>
      )}
    </>
  );
};

export default PersonalMiscNumbersInfo;